import { useCallback, useMemo, useState } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Modal } from '../../UI';
import { WeekCalendar } from '../WeekCalendar/WeekCalendar';
import { InterventionDetailPopover } from '../InterventionDetailPopover/InterventionDetailPopover';
import { CalendarFilterSidebar } from './CalendarFilterSidebar';
import type { ActiveFilterSummary } from './CalendarFilterSidebar';
import { patientDisplayName } from '../../../services/patientLabelService';
import { CalendarWeekNavBar } from './CalendarWeekNavBar';
import { formatLongDate } from '../../../utils/weekDateUtils';
import type {
  Intervention,
  WeekData,
  WeekFilterState,
  WeekNavigation
} from '../../../types/weekOverview';
import styles from './IndividualCalendarModal.module.css';

interface IndividualCalendarModalProps {
  weekData: WeekData;
  filters: WeekFilterState;
  onFiltersChange: (filters: WeekFilterState) => void;
  onClose: () => void;
  // Omitted by the year view, which opens the calendar for one fixed week.
  navigation?: WeekNavigation;
}

interface InterventionSelection {
  intervention: Intervention;
  anchorRect: DOMRect;
}

const MODAL_WIDTH_PX = 1600;
const MODAL_MAX_WIDTH = '96vw';

// Drops every filter id the given week's roster cannot resolve. Filters
// persist across week navigation, but each week carries its own roster — if
// the raw state were applied, a stale id would keep filtering the canvas
// invisibly while the sidebar summary (which only lists resolvable ids) shows
// no active filter.
const resolveFiltersAgainstWeek = (
  filters: WeekFilterState,
  weekData: WeekData
): WeekFilterState => ({
  staffId: weekData.staff.some((s) => s.id === filters.staffId) ? filters.staffId : null,
  roomId: weekData.rooms.some((r) => r.id === filters.roomId) ? filters.roomId : null,
  patientId: weekData.patients.some((p) => p.id === filters.patientId) ? filters.patientId : null
});

export const IndividualCalendarModal = ({
  weekData,
  filters,
  onFiltersChange,
  onClose,
  navigation
}: IndividualCalendarModalProps) => {
  const { t, i18n } = useTranslation();
  // The clicked card's anchor rect positions the detail popover beside it.
  const [selection, setSelection] = useState<InterventionSelection | null>(null);
  const [isPrinting, setIsPrinting] = useState(false);

  const handleSelectIntervention = useCallback((intervention: Intervention, anchorRect: DOMRect) => {
    setSelection({ intervention, anchorRect });
  }, []);

  const handleClosePopover = useCallback(() => {
    setSelection(null);
  }, []);

  // Escape steps back one layer at a time: an open detail popover closes
  // first, the modal itself only closes when no popover is open.
  const handleModalClose = useCallback(() => {
    if (selection) {
      setSelection(null);
      return;
    }
    onClose();
  }, [selection, onClose]);

  // Changing the week reuses the page's navigation, but an open selection
  // refers to the previous week's data — clear it before navigating.
  const navigationClearingSelection = useMemo<WeekNavigation | undefined>(() => {
    if (!navigation) return undefined;
    const clearThen = (navigate: () => void) => (): void => {
      setSelection(null);
      navigate();
    };
    return {
      isViewingCurrentWeek: navigation.isViewingCurrentWeek,
      onPreviousWeek: clearThen(navigation.onPreviousWeek),
      onNextWeek: clearThen(navigation.onNextWeek),
      onGoToToday: clearThen(navigation.onGoToToday)
    };
  }, [navigation]);

  // Clear any selection, switch the calendar to its compact print layout
  // (synchronously, so it's committed before the print dialog captures it),
  // print — the dialog offers "Save as PDF" — then restore the screen layout.
  const handlePrint = useCallback(() => {
    setSelection(null);
    flushSync(() => setIsPrinting(true));
    window.print();
    setIsPrinting(false);
  }, []);

  const handleFiltersChange = useCallback(
    (next: WeekFilterState) => {
      setSelection(null);
      onFiltersChange(next);
    },
    [onFiltersChange]
  );

  // Every consumer below (canvas filtering, summary, sidebar dropdowns) reads
  // the resolved filters, never the raw prop, so they cannot disagree about
  // which filters are active (see resolveFiltersAgainstWeek).
  const resolvedFilters = useMemo(
    () => resolveFiltersAgainstWeek(filters, weekData),
    [filters, weekData]
  );

  const filteredInterventions = useMemo(() => {
    // The unplanned shell carries no interventions — the canvas shows the
    // unplanned empty state instead.
    if (!weekData.isPlanned) return [];
    return weekData.interventions.filter(
      (i) =>
        (!resolvedFilters.roomId || i.roomId === resolvedFilters.roomId) &&
        (!resolvedFilters.staffId || i.staffIds.includes(resolvedFilters.staffId)) &&
        (!resolvedFilters.patientId || i.patientIds.includes(resolvedFilters.patientId))
    );
  }, [weekData, resolvedFilters]);

  // The three filters combine with AND, so more than one can be active at once.
  // Each active condition is listed in the summary with its type and value.
  const activeFilters = useMemo(() => {
    const list: ActiveFilterSummary[] = [];
    if (resolvedFilters.staffId) {
      const name = weekData.staff.find((s) => s.id === resolvedFilters.staffId)?.name;
      if (name) list.push({ type: 'staff', typeLabel: t('week-overview-detail-staff'), value: name });
    }
    if (resolvedFilters.roomId) {
      const name = weekData.rooms.find((r) => r.id === resolvedFilters.roomId)?.name;
      if (name) list.push({ type: 'room', typeLabel: t('week-overview-detail-room'), value: name });
    }
    if (resolvedFilters.patientId) {
      const patient = weekData.patients.find((p) => p.id === resolvedFilters.patientId);
      // An empty label (a real patient with a blank name) is skipped, so the
      // modal title and print header never render a dangling separator.
      const label = patient ? patientDisplayName(patient, t) : '';
      if (label) {
        list.push({
          type: 'patient',
          typeLabel: t('week-overview-detail-patients'),
          value: label
        });
      }
    }
    return list;
  }, [resolvedFilters, weekData.staff, weekData.rooms, weekData.patients, t]);

  const hasResourceSelected = activeFilters.length > 0;
  const activeFilterValues = activeFilters.map((f) => f.value).join(' · ');

  const modalTitle = hasResourceSelected
    ? `${t('week-overview-modal-title')} · ${activeFilterValues}`
    : t('week-overview-modal-title');

  const weekRangeLabel = `${formatLongDate(new Date(weekData.weekStartDate), i18n.language)} – ${formatLongDate(
    new Date(weekData.weekEndDate),
    i18n.language
  )}`;

  return (
    <Modal
      title={modalTitle}
      onClose={handleModalClose}
      width={MODAL_WIDTH_PX}
      style={{ maxWidth: MODAL_MAX_WIDTH }}
      contentStyle={{ padding: 0 }}
    >
      <div className={styles.body}>
        <CalendarFilterSidebar
          staff={weekData.staff}
          rooms={weekData.rooms}
          patients={weekData.patients}
          filters={resolvedFilters}
          activeFilters={activeFilters}
          filteredEventCount={filteredInterventions.length}
          onFiltersChange={handleFiltersChange}
        />

        <div className={styles.canvas}>
          <CalendarWeekNavBar
            weekNumber={weekData.weekNumber}
            weekRangeLabel={weekRangeLabel}
            canPrint={hasResourceSelected && weekData.isPlanned}
            navigation={navigationClearingSelection}
            onPrint={handlePrint}
          />
          {!weekData.isPlanned ? (
            <div className={styles.emptyState}>
              <p className={styles.emptyStateTitle}>{t('week-overview-unplanned-title')}</p>
              <p>{t('week-overview-unplanned-description')}</p>
            </div>
          ) : hasResourceSelected ? (
            <div className={`${styles.printRegion} js-print-region`}>
              <div className={styles.printHeader}>
                <h1 className={styles.printTitle}>{activeFilterValues}</h1>
                <p className={styles.printMeta}>
                  {t('week-overview-week-label', { weekNumber: weekData.weekNumber })} · {weekRangeLabel}{' '}
                  · {weekData.locationName}
                </p>
              </div>
              <WeekCalendar
                weekData={weekData}
                filteredInterventions={filteredInterventions}
                selectedInterventionId={selection?.intervention.id ?? null}
                printMode={isPrinting}
                onInterventionClick={handleSelectIntervention}
              />
            </div>
          ) : (
            <div className={styles.emptyState}>{t('week-overview-modal-empty')}</div>
          )}
        </div>
      </div>
      {selection && (
        <InterventionDetailPopover
          intervention={selection.intervention}
          weekData={weekData}
          anchorRect={selection.anchorRect}
          onClose={handleClosePopover}
        />
      )}
    </Modal>
  );
};
